<script setup>
import { computed, defineProps, onMounted, ref, watch } from "vue";
import { useListStoreNew } from "@/stores/ListStoreNew";
import PrevNextButtons from "@/components/PrevNextButtons.vue";
import ListSearch from "@/components/ListSearch.vue";
import {
  FlagIcon,
  PlusCircleIcon,
} from "@heroicons/vue/24/solid";
import TicketService from "@/services/TicketService";
import { useRoute, useRouter } from "vue-router";
import { usePagination } from "@/composables/usePagination";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";
import { useTicketStore } from "@/stores/TicketStore";
import dayjs from "dayjs";

var relativeTime = require("dayjs/plugin/relativeTime");
dayjs.extend(relativeTime);

const list = useListStoreNew();
list.$reset();
const ticketStore = useTicketStore();
const counters = ref({});
const route = useRoute();
const router = useRouter();
const filter = ref("needs_reply");
const ticketService = new TicketService();

const { setUrl } = usePagination(list, filter);
const { setBreadcrumb } = useBreadcrumbs();
const props = defineProps({
  showSearch: { type: Boolean, required: false, default: true },
  showLabel: { type: Boolean, required: false, default: true },
  showName: { type: Boolean, required: false, default: true },
  showDate: { type: Boolean, required: false, default: true },
});
const columnCount = computed(
  () =>
    [
      props.showLabel,
      props.showName,
      true,
      props.showDate,
      true,
    ].filter(Boolean).length,
);
const sectionCount = (section, fallbackKey = null) =>
  counters.value.sections?.[section] ??
  (fallbackKey ? counters.value[fallbackKey] : undefined);

const filterButtons = computed(() => [
  {
    value: "needs_reply",
    label: "Needs Reply",
    count: sectionCount("needs_reply", "unanswered"),
    activeClasses: "border-cyan-600 bg-cyan-600 text-white shadow-sm dark:border-cyan-400 dark:bg-cyan-400 dark:text-slate-900",
    idleClasses: "border-cyan-200 bg-cyan-50 text-cyan-800 hover:bg-cyan-100 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-200 dark:hover:bg-cyan-900/50",
  },
  {
    value: "follow_up",
    label: "Follow-up",
    count: sectionCount("follow_up", "flagged"),
    activeClasses: "border-amber-500 bg-amber-500 text-white shadow-sm dark:border-amber-400 dark:bg-amber-400 dark:text-slate-900",
    idleClasses: "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:hover:bg-amber-900/40",
  },
  {
    value: "open",
    label: "Open",
    count: sectionCount("open"),
    activeClasses: "border-slate-900 bg-slate-900 text-white shadow-sm dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900",
    idleClasses: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700",
  },
  {
    value: "all",
    label: "All Tickets",
    count: sectionCount("all", "total"),
    activeClasses: "border-sky-600 bg-sky-600 text-white shadow-sm dark:border-sky-400 dark:bg-sky-400 dark:text-slate-900",
    idleClasses: "border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-200 dark:hover:bg-sky-900/40",
  },
  {
    value: "closed",
    label: "Closed",
    count: sectionCount("closed"),
    activeClasses: "border-slate-500 bg-slate-500 text-white shadow-sm dark:border-slate-400 dark:bg-slate-400 dark:text-slate-900",
    idleClasses: "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300 dark:hover:bg-slate-800",
  },
  {
    value: "spam",
    label: "Spam",
    count: sectionCount("spam"),
    activeClasses: "border-rose-600 bg-rose-600 text-white shadow-sm dark:border-rose-400 dark:bg-rose-400 dark:text-slate-900",
    idleClasses: "border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200 dark:hover:bg-rose-900/40",
  },
]);

onMounted(async () => {
  list.configure({ remote: "/api/ticket", local: "/tickets" });
  syncFilterFromRoute();
  applyFilter();

  const listLoad = loadTicketIndex();
  void refreshCounters();
  await listLoad;
});

watch(
  () => ticketStore.listRefreshKey,
  async () => {
    if (route.path !== "/tickets" || !list.listService) {
      return;
    }

    syncFilterFromRoute();
    applyFilter();
    await Promise.all([loadTicketIndex(), refreshCounters()]);
  },
);

function syncFilterFromRoute() {
  filter.value = normaliseFilter(route.query?.af);
}

const applyFilter = () => {
  list.clearFilters();
  list.addFilter("summary", "1");
  list.addFilter("section", filter.value);
};

const setFilter = (val) => {
  if (filter.value === val && list.getPage === 1) {
    return;
  }

  filter.value = val;
  list.resetPageWithoutFetching();
  applyFilter();
  setUrl();
};

const loadTicketIndex = async () => {
  await list.fetchIndex();
  monitorNoUnanswered();
};

const refreshCounters = async () => {
  try {
    const counterData = await ticketService.counters();
    counters.value = counterData ?? {};
    ticketStore.setUnansweredCount(counterData?.unanswered ?? 0);
  } catch (error) {
    console.error(error);
  }
};

const monitorNoUnanswered = () => {
  setTimeout(() => {
    if (!list.isLoading && list.getIndex.length < 1 && filter.value === "needs_reply") {
      window.triggerConfetti();
    }
  }, 1000);
};

const normaliseFilter = (value) => {
  const aliases = {
    unanswered: "needs_reply",
    flagged: "follow_up",
  };
  const nextValue = aliases[value] ?? value;
  const sectionValues = filterButtons.value.map((button) => button.value);

  return sectionValues.includes(nextValue) ? nextValue : "needs_reply";
};

const latestTicketItem = (row) => {
  if (row?.latest_item) {
    return row.latest_item;
  }

  const items = Array.isArray(row?.items) ? row.items : [];
  return items.length ? items[items.length - 1] : null;
};

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const lastExternalSender = (row) => {
  const latestItem = latestTicketItem(row);

  return (
    row?.latest_sender_name ||
    latestItem?.contact?.name ||
    latestItem?.contact_name ||
    latestItem?.user?.name ||
    latestItem?.user_name ||
    "n/a"
  );
};

const lastMessagePreview = (row) => {
  const latestItem = latestTicketItem(row);
  const preview = String(
    row?.last_message_preview ||
      latestItem?.text ||
      stripHtml(latestItem?.html) ||
      "",
  )
    .replace(/\s+/g, " ")
    .trim();

  if (!preview) {
    return "No message preview available yet.";
  }

  return preview.length > 120 ? `${preview.slice(0, 117)}...` : preview;
};

const spamPurgeLabel = (row) => {
  if (row?.spam_purge_blocked_at) {
    return "Deletion blocked — review";
  }

  if (!row?.spam_purge_at) {
    return "Pending deletion";
  }

  const days = Math.max(0, Math.ceil(dayjs(row.spam_purge_at).diff(dayjs(), "hour", true) / 24));
  return days === 0 ? "Deletes today" : `Deletes in ${days} day${days === 1 ? "" : "s"}`;
};

const channelLabel = (channel) => {
  switch (channel) {
    case "events":
      return "Event";
    case "transport":
      return "Transport";
    case "vacancies":
    case "job":
      return "Job";
    case "junk":
      return "Junk";
    case "finance":
    case "invoices":
      return "Finance";
    default:
      return "General";
  }
};

const channelAbbreviation = (channel) => {
  switch (channel) {
    case "events":
      return "Evt";
    case "transport":
      return "Trn";
    case "vacancies":
    case "job":
      return "Job";
    case "junk":
      return "Jnk";
    case "finance":
    case "invoices":
      return "Fin";
    default:
      return "Gen";
  }
};

const filterButtonClasses = (value, activeClasses, idleClasses) =>
  [
    "flex items-center justify-between rounded-2xl border px-4 py-3 text-sm font-semibold transition-colors",
    filter.value === value ? activeClasses : idleClasses,
  ].join(" ");

const changeTicketPage = (delta) => {
  list.changePageWithoutFetching(delta);
  setUrl();
};

const view = (val, event = {}) => {
  if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) {
    return;
  }

  router.push({
    name: "tickets-view",
    params: { id: val.id },
    query: route.query,
  });
  setBreadcrumb(val.subject);
};
</script>
<template>
  <div>
    <div class="box space-y-4" v-show="props.showSearch">
      <div class="flex w-full items-center gap-3">
        <ListSearch
          class="min-w-0 flex-1"
          url="/api/ticket/search?query="
          :output="(val) => `${val.subject} [${lastExternalSender(val)}]`"
          @selected="view"
        />
        <router-link to="/tickets/create" class="btn accent shrink-0" title="Create New">
          <PlusCircleIcon class="icon" />
        </router-link>
      </div>

      <div class="grid grid-cols-1 gap-2 md:grid-cols-3 xl:grid-cols-6">
        <button
          v-for="button in filterButtons"
          :key="button.value"
          :class="filterButtonClasses(button.value, button.activeClasses, button.idleClasses)"
          @click.prevent="setFilter(button.value)"
        >
          <span>{{ button.label }}</span>
          <span class="text-xs">{{ button.count ?? 0 }}</span>
        </button>
      </div>

      <div class="flex flex-wrap gap-3 text-xs text-slate-500 dark:text-slate-400">
        <span>Today: {{ counters.summary?.today ?? 0 }}</span>
        <span>This month: {{ counters.summary?.month ?? 0 }}</span>
        <span>This year: {{ counters.summary?.year ?? 0 }}</span>
      </div>
    </div>

    <template v-if="!list.isLoading">
      <table class="box list hidden w-full table-auto overflow-hidden xl:table">
        <thead class="border-b border-slate-200 bg-slate-50 text-left text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
          <tr>
            <th v-show="props.showLabel" class="w-16 whitespace-nowrap p-2.5 font-semibold sm:p-3">Type</th>
            <th v-show="props.showName" class="hidden w-40 whitespace-nowrap p-2.5 font-semibold lg:table-cell lg:p-3">From</th>
            <th class="w-auto p-2.5 font-semibold sm:p-3">Ticket</th>
            <th
              v-show="props.showDate"
              class="hidden w-28 whitespace-nowrap p-2.5 text-right font-semibold lg:table-cell lg:p-3"
            >
              Updated
            </th>
            <th class="hidden w-32 whitespace-nowrap p-2.5 text-right font-semibold xl:table-cell xl:p-3">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in list.getIndex"
            :key="row.id"
            class="cursor-pointer border-b border-slate-200 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/60"
            :class="[
              row.spam_marked_at ? 'border-l-4 border-l-rose-500 bg-rose-50/40 dark:bg-rose-950/20' : '',
              !row.spam_marked_at && row.awaitingResponse ? 'border-l-4 border-l-cyan-500 bg-cyan-50/40 dark:bg-cyan-950/20' : '',
              !row.spam_marked_at && row.flagged ? 'bg-amber-50/40 dark:bg-amber-950/20' : '',
            ]"
            @click.prevent="view(row, $event)"
          >
            <td class="w-16 whitespace-nowrap p-2.5 text-center align-top sm:p-3" v-show="props.showLabel">
              <label class="label-event" v-if="row.channel == 'events'">
                <span class="hidden xl:inline">Event</span>
                <span class="xl:hidden">{{ channelAbbreviation(row.channel) }}</span>
              </label>
              <label class="label-transport" v-else-if="row.channel == 'transport'">
                <span class="hidden xl:inline">Transport</span>
                <span class="xl:hidden">{{ channelAbbreviation(row.channel) }}</span>
              </label>
              <label class="label-job" v-else-if="row.channel == 'vacancies' || row.channel == 'job'">
                <span class="hidden xl:inline">Job</span>
                <span class="xl:hidden">{{ channelAbbreviation(row.channel) }}</span>
              </label>
              <label class="label-junk" v-else-if="row.channel == 'junk'">
                <span class="hidden xl:inline">Junk</span>
                <span class="xl:hidden">{{ channelAbbreviation(row.channel) }}</span>
              </label>
              <label class="label-finance" v-else-if="row.channel == 'finance' || row.channel == 'invoices'">
                <span class="hidden xl:inline">Finance</span>
                <span class="xl:hidden">{{ channelAbbreviation(row.channel) }}</span>
              </label>
              <label class="label-bubble" v-else>
                <span class="hidden xl:inline">{{ channelLabel(row.channel) }}</span>
                <span class="xl:hidden">{{ channelAbbreviation(row.channel) }}</span>
              </label>
            </td>
            <td class="hidden w-40 whitespace-nowrap p-2.5 align-top lg:table-cell lg:p-3" v-show="props.showName">
              <div class="max-w-40 truncate font-medium text-slate-800 dark:text-slate-100" :title="lastExternalSender(row)">
                {{ lastExternalSender(row) }}
              </div>
              <div class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Ticket #{{ row.id }}
              </div>
            </td>
            <td class="min-w-0 p-2.5 sm:p-3">
              <div class="truncate font-semibold text-slate-900 dark:text-slate-100">
                {{ row.subject }}
              </div>
              <div class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 dark:text-slate-400 lg:hidden">
                <span class="truncate max-w-full">{{ lastExternalSender(row) }}</span>
                <span>{{ dayjs(row.updated_at).fromNow() }}</span>
              </div>
              <div class="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-slate-300">
                {{ lastMessagePreview(row) }}
              </div>
              <div class="mt-2 flex flex-wrap gap-2 xl:hidden">
                <span
                  v-if="!row.spam_marked_at && row.awaitingResponse"
                  class="inline-flex rounded-full bg-cyan-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-800 dark:bg-cyan-900/60 dark:text-cyan-100"
                >
                  Needs reply
                </span>
                <span
                  v-if="!row.spam_marked_at && row.flagged"
                  class="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-900 dark:bg-amber-900/60 dark:text-amber-100"
                >
                  <FlagIcon class="h-3.5 w-3.5" />
                  Flagged
                </span>
                <span
                  v-if="row.spam_marked_at"
                  class="inline-flex rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-rose-800 dark:bg-rose-900/60 dark:text-rose-100"
                >
                  {{ row.spam_source === "automatic" ? "Auto spam" : "Spam" }}
                </span>
              </div>
            </td>
            <td
              class="hidden w-28 whitespace-nowrap p-2.5 text-right align-top lg:table-cell lg:p-3"
              v-show="props.showDate"
            >
              {{ dayjs(row.updated_at).fromNow() }}
            </td>
            <td class="hidden w-32 whitespace-nowrap p-2.5 text-right align-top xl:table-cell xl:p-3">
              <div class="flex flex-col items-end gap-2">
                <span
                  v-if="!row.spam_marked_at && row.awaitingResponse"
                  class="inline-flex rounded-full bg-cyan-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-800 dark:bg-cyan-900/60 dark:text-cyan-100"
                >
                  Needs reply
                </span>
                <span
                  v-if="!row.spam_marked_at && row.flagged"
                  class="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-900 dark:bg-amber-900/60 dark:text-amber-100"
                >
                  <FlagIcon class="h-3.5 w-3.5" />
                  Flagged
                </span>
                <span
                  v-if="row.spam_marked_at"
                  class="inline-flex rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-rose-800 dark:bg-rose-900/60 dark:text-rose-100"
                >
                  {{ row.spam_source === "automatic" ? "Auto spam" : "Spam" }}
                </span>
                <span v-if="row.spam_marked_at" class="text-[11px] text-rose-700 dark:text-rose-300">
                  {{ spamPurgeLabel(row) }}
                </span>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td :colspan="columnCount" class="p-4" v-if="list.getIndex.length < 1">
              No data to display
            </td>
          </tr>
          <tr>
            <th :colspan="columnCount" class="p-4">
              <PrevNextButtons
                :showPrev="list.showPrev"
                :showNext="list.showNext"
                @prev="changeTicketPage(-1)"
                @next="changeTicketPage(1)"
              />
            </th>
          </tr>
        </tfoot>
      </table>

      <div class="box divide-y divide-slate-200 overflow-hidden dark:divide-slate-700 xl:hidden">
        <button
          v-for="row in list.getIndex"
          :key="row.id"
          type="button"
          class="block w-full bg-white p-3 text-left transition-colors hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-700/60"
          :class="[
            row.spam_marked_at ? 'border-l-4 border-l-rose-500 bg-rose-50/30 dark:bg-rose-950/20' : '',
            !row.spam_marked_at && row.awaitingResponse ? 'border-l-4 border-l-cyan-500' : '',
            !row.spam_marked_at && row.flagged ? 'bg-amber-50/30 dark:bg-amber-950/20' : '',
          ]"
          @click.prevent="view(row, $event)"
        >
          <div class="flex items-start gap-3">
            <div v-show="props.showLabel" class="shrink-0 pt-0.5">
              <label class="label-event" v-if="row.channel == 'events'">
                {{ channelAbbreviation(row.channel) }}
              </label>
              <label class="label-transport" v-else-if="row.channel == 'transport'">
                {{ channelAbbreviation(row.channel) }}
              </label>
              <label class="label-job" v-else-if="row.channel == 'vacancies' || row.channel == 'job'">
                {{ channelAbbreviation(row.channel) }}
              </label>
              <label class="label-junk" v-else-if="row.channel == 'junk'">
                {{ channelAbbreviation(row.channel) }}
              </label>
              <label class="label-finance" v-else-if="row.channel == 'finance' || row.channel == 'invoices'">
                {{ channelAbbreviation(row.channel) }}
              </label>
              <label class="label-bubble" v-else>
                {{ channelAbbreviation(row.channel) }}
              </label>
            </div>

            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-start justify-between gap-2">
                <div class="min-w-0 flex-1">
                  <div class="truncate font-semibold text-slate-900 dark:text-slate-100">
                    {{ row.subject }}
                  </div>
                  <div class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                    <span v-show="props.showName" class="truncate max-w-full">{{ lastExternalSender(row) }}</span>
                    <span v-show="props.showDate">{{ dayjs(row.updated_at).fromNow() }}</span>
                    <span class="whitespace-nowrap">Ticket #{{ row.id }}</span>
                  </div>
                </div>

                <div class="flex shrink-0 flex-wrap justify-end gap-2">
                  <span
                    v-if="!row.spam_marked_at && row.awaitingResponse"
                    class="inline-flex rounded-full bg-cyan-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-800 dark:bg-cyan-900/60 dark:text-cyan-100"
                  >
                    Needs reply
                  </span>
                  <span
                    v-if="!row.spam_marked_at && row.flagged"
                    class="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-900 dark:bg-amber-900/60 dark:text-amber-100"
                  >
                    <FlagIcon class="h-3.5 w-3.5" />
                    Flagged
                  </span>
                  <span
                    v-if="row.spam_marked_at"
                    class="inline-flex rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-rose-800 dark:bg-rose-900/60 dark:text-rose-100"
                  >
                    {{ row.spam_source === "automatic" ? "Auto spam" : "Spam" }}
                  </span>
                </div>
              </div>

              <div class="mt-2 line-clamp-2 text-sm text-slate-600 dark:text-slate-300">
                {{ lastMessagePreview(row) }}
              </div>
              <div v-if="row.spam_marked_at" class="mt-2 text-xs font-medium text-rose-700 dark:text-rose-300">
                {{ spamPurgeLabel(row) }}
              </div>
            </div>
          </div>
        </button>

        <div class="p-4" v-if="list.getIndex.length < 1">
          No data to display
        </div>

        <div class="bg-slate-50/70 p-4 dark:bg-slate-900/40">
          <PrevNextButtons
            :showPrev="list.showPrev"
            :showNext="list.showNext"
            @prev="changeTicketPage(-1)"
            @next="changeTicketPage(1)"
          />
        </div>
      </div>
    </template>

    <template v-else>
      <table class="box list hidden w-full table-auto xl:table">
        <tbody>
          <tr v-for="n in 24" :key="n" class="animate-pulse border-b border-gray-300 dark:border-slate-700">
            <td class="w-16 whitespace-nowrap p-2.5 text-center sm:p-3">
              <div class="h-6 rounded bg-gray-300 dark:bg-slate-700"></div>
            </td>
            <td class="hidden w-40 whitespace-nowrap p-2.5 lg:table-cell lg:p-3">
              <div class="h-6 w-32 rounded bg-gray-300 dark:bg-slate-700"></div>
            </td>
            <td class="p-2.5 sm:p-3">
              <div class="h-6 rounded bg-gray-300 dark:bg-slate-700"></div>
              <div class="mt-2 h-4 rounded bg-gray-200 dark:bg-slate-600"></div>
              <div class="mt-2 h-5 w-24 rounded-full bg-gray-200 dark:bg-slate-600 xl:hidden"></div>
            </td>
            <td class="hidden w-28 whitespace-nowrap p-2.5 text-right lg:table-cell lg:p-3">
              <div class="ml-auto h-6 w-20 rounded bg-gray-300 dark:bg-slate-700"></div>
            </td>
            <td class="hidden w-32 whitespace-nowrap p-2.5 text-right xl:table-cell xl:p-3">
              <div class="ml-auto h-5 w-16 rounded-full bg-gray-200 dark:bg-slate-600"></div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="box divide-y divide-slate-200 overflow-hidden dark:divide-slate-700 xl:hidden">
        <div v-for="n in 24" :key="n" class="p-3">
          <div class="flex items-start gap-3 animate-pulse">
            <div class="h-6 w-10 shrink-0 rounded bg-gray-300 dark:bg-slate-700"></div>
            <div class="min-w-0 flex-1">
              <div class="h-5 rounded bg-gray-300 dark:bg-slate-700"></div>
              <div class="mt-2 h-4 w-2/3 rounded bg-gray-200 dark:bg-slate-600"></div>
              <div class="mt-2 h-4 rounded bg-gray-200 dark:bg-slate-600"></div>
              <div class="mt-2 h-5 w-24 rounded-full bg-gray-200 dark:bg-slate-600"></div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
</style>
